'use client';

import { Content } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import Marquee from 'react-fast-marquee';
import { useEffect, useState } from 'react';
import { ArrowRightIcon } from '@/assets';
import Image from 'next/image';
import StarfieldBackground from '@/components/StarfieldBackground';
import WaveGridBackground from '@/components/WaveGridBackground';
import ParticlesBackground from '@/components/ParticlesBackground';
import WithLogoOnTop from './variations/WithLogoOnTop';
import WithStats from '@/slices/HeroCtaWithLogos/variations/WithStats';
import { useModalStore } from '@/store';

export type HeroCtaWithLogosProps =
  SliceComponentProps<Content.HeroCtaWithLogosSlice>;

const HeroCtaWithLogos = ({
  slice,
}: HeroCtaWithLogosProps): JSX.Element | null | undefined => {
  const [isPaused, setIsPaused] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const { openModal } = useModalStore();

  useEffect(() => {
    setIsClient(true);
  }, []);

  if (slice.variation === 'noLogo1CtaStarBackgroundH') {
    const { title, description, button_link, button_text, animated_gif } =
      slice.primary || {};
    if (!title && !description && !button_text && !button_link) return null;

    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="relative text-white min-h-screen flex items-center justify-center"
      >
        <StarfieldBackground />
        <div className="relative z-10 container mx-auto px-4 md:px-6 flex flex-col items-center text-center gap-8">
          <div className="max-w-4xl flex flex-col gap-6">
            <div className="font-bold text-2xl md:text-4xl">
              <PrismicRichText field={title} />
            </div>
            <div className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
              <PrismicRichText field={description} />
            </div>
          </div>

          <div className="flex flex-col items-center justify-center mt-8">
            <PrismicNextLink field={button_link} className="secondary-button">
              <ArrowRightIcon className="arrow-icon" />
              {button_text}
            </PrismicNextLink>
            {animated_gif?.url && (
              <div className="mt-8">
                <Image
                  src={animated_gif.url}
                  alt={animated_gif.alt || 'Animated GIF'}
                  width={
                    animated_gif.dimensions?.width
                      ? animated_gif.dimensions.width / 3
                      : 800
                  }
                  height={
                    animated_gif.dimensions?.height
                      ? animated_gif.dimensions.height / 3
                      : 800
                  }
                  className="max-w-full h-auto mx-auto"
                />
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  // Variation: singleCtaWithAnimation
  if (slice.variation === 'singleCtaWithAnimation') {
    const { title, description, button_link, button_text, animated_gif } =
      slice.primary || {};
    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="relative text-white pt-48 pb-12 md:pt-48 md:pb-40 overflow-hidden"
      >
        <WaveGridBackground />
        <div className="relative z-10 container mx-auto flex flex-col items-center text-center gap-8">
          <div className="max-w-4xl flex flex-col gap-6">
            <div className="font-bold text-4xl md:text-6xl">
              <PrismicRichText field={title} />
            </div>
            <div className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
              <PrismicRichText field={description} />
            </div>
          </div>
          <div className="flex flex-col items-center justify-center mt-8">
            <PrismicNextLink field={button_link} className="secondary-button">
              <ArrowRightIcon className="arrow-icon" />
              {button_text}
            </PrismicNextLink>
            {animated_gif?.url && (
              <div className="mt-8">
                <Image
                  src={animated_gif.url}
                  alt={animated_gif.alt || 'Animated GIF'}
                  width={
                    animated_gif.dimensions?.width
                      ? animated_gif.dimensions.width / 3
                      : 800
                  }
                  height={
                    animated_gif.dimensions?.height
                      ? animated_gif.dimensions.height / 3
                      : 800
                  }
                  className="max-w-full h-auto mx-auto"
                />
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  if (slice.variation === 'withAvatarsProfile') {
    const { title, description, buttons, profiles } = slice.primary || {};

    type Profile = {
      avatar_title?: string;
      avatar_description?: string;
      avatar_image?: unknown;
    };
    const renderProfileCard = (item: unknown, uniqueKey: unknown) => {
      const profile = item as Profile;
      const match = String(uniqueKey).match(/-(\d+)-/);
      const index = match ? parseInt(match[1]) : 0;
      const isEven = index % 2 === 0;

      return (
        <div
          key={String(uniqueKey)}
          className="flex-shrink-0 flex flex-col items-center text-center mx-4"
        >
          <div
            className={`flex flex-col items-center ${isEven ? 'mb-8' : 'mt-8'}`}
          >
            {!isEven && (
              <div className="mb-4">
                <p className="font-semibold text-sm text-white">
                  {profile.avatar_title}
                </p>
                {profile.avatar_description && (
                  <p className="text-xs text-slate-300">
                    {profile.avatar_description}
                  </p>
                )}
              </div>
            )}

            <div className="relative w-28 h-28 md:w-36 md:h-36">
              <PrismicNextImage
                field={profile.avatar_image as object}
                className="rounded-2xl object-cover"
                fill
                sizes="(max-width: 768px) 112px, 144px"
              />
            </div>

            {isEven && (
              <div className="mt-4">
                <p className="font-semibold text-sm text-white">
                  {profile.avatar_title}
                </p>
                {profile.avatar_description && (
                  <p className="text-xs text-slate-300">
                    {profile.avatar_description}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      );
    };

    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation || 'default'}
        className="relative text-white pt-48 pb-24 md:pt-48 md:pb-40 overflow-hidden"
      >
        <ParticlesBackground />

        <div className="relative z-10 mx-auto flex flex-col items-center text-center gap-8">
          <div className="px-4 md:px-32 flex flex-col gap-6">
            <div className="font-bold text-4xl md:text-6xl">
              <PrismicRichText
                field={title}
                components={{
                  heading1: ({ children }) => (
                    <h1 className="h1-hero">{children}</h1>
                  ),
                }}
              />
            </div>
            <div className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
              <PrismicRichText field={description} />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {buttons?.map((item, index) => (
              <PrismicNextLink
                key={index}
                field={item.button_link}
                className={index === 0 ? 'secondary-button' : 'tertiary-button'}
              >
                <ArrowRightIcon className="arrow-icon" />
                {item.button_text}
              </PrismicNextLink>
            ))}
          </div>

          {/* Avatars Scroll */}
          {profiles?.length > 0 && (
            <div className="relative w-full overflow-hidden py-8">
              <div className="flex min-w-[200%] whitespace-nowrap animate-scroll-left gap-x-8 mt-12">
                {profiles.map((item, index) =>
                  renderProfileCard(
                    item,
                    `${item.avatar_title}-${index}-first`,
                  ),
                )}
                {profiles.map((item, index) =>
                  renderProfileCard(
                    item,
                    `${item.avatar_title}-${index}-second`,
                  ),
                )}
                {profiles.map((item, index) =>
                  renderProfileCard(
                    item,
                    `${item.avatar_title}-${index}-third`,
                  ),
                )}
              </div>

              <style jsx>{`
                @keyframes scroll-left {
                  0% {
                    transform: translateX(0%);
                  }
                  100% {
                    transform: translateX(-50%);
                  }
                }

                .animate-scroll-left {
                  animation: scroll-left ${profiles.length * 4}s linear infinite;
                }
              `}</style>
            </div>
          )}
        </div>
      </section>
    );
  }

  if (slice.variation === 'withLogoOnTop') {
    return (
      <WithLogoOnTop
        slice={slice}
        title={slice.primary.title}
        image={slice.primary.logo}
      />
    );
  }

  if (slice.variation === 'withStats') {
    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="relative text-white pt-48 pb-24 md:pt-48 md:pb-40 overflow-hidden"
      >
        {slice.primary.background_image?.url ? (
          <>
            <Image
              src={slice.primary.background_image.url}
              alt={slice.primary.background_image.alt || 'Background'}
              fill
              className="absolute inset-0 object-cover w-full h-full z-0"
            />
            <div className="absolute inset-0 z-0 bg-gradient-to-b from-[#0F423A]/75 via-[#0F423A]/85 to-[#0F423A]/95" />
          </>
        ) : (
          <div className="absolute inset-0 z-0">
            <ParticlesBackground />
          </div>
        )}

        <div className="relative z-10">
          <WithStats slice={slice} />
        </div>
      </section>
    );
  }

  if (slice.variation === 'default') {
    // Default Variation (from staging with improvements)
    const { title, description, buttons, background_image } =
      slice.primary || {};
    const doubledLogos = [
      ...(slice.primary.logos || []),
      ...(slice.primary.logos || []),
    ];

    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation || 'default'}
        className="relative text-white pt-48 pb-24 md:pt-48 md:pb-40 overflow-hidden"
      >
        {background_image?.url ? (
          <>
            <Image
              src={background_image.url}
              alt={background_image.alt || 'Background'}
              fill
              className="absolute inset-0 object-cover w-full h-full z-0"
            />
            <div className="absolute inset-0 z-0 bg-gradient-to-b from-[#0F423A]/75 via-[#0F423A]/85 to-[#0F423A]/95" />
          </>
        ) : (
          <StarfieldBackground />
        )}

        <div className="relative z-10 container mx-auto flex flex-col items-center text-center gap-8">
          <div className="px-4 md:px-32 flex flex-col gap-6">
            <div className="font-bold text-4xl md:text-6xl">
              <PrismicRichText field={title} />
            </div>
            <div className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
              <PrismicRichText field={description} />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {buttons
              ?.filter((item) => item.button_text)
              .map((item, index) => {
                const handleClick = (e: React.MouseEvent) => {
                  if (item.open_modal) {
                    e.preventDefault();
                    openModal();
                  }
                };

                const classes =
                  index === 0 ? 'secondary-button' : 'tertiary-button';

                if (item.open_modal) {
                  return (
                    <button
                      key={index}
                      onClick={handleClick}
                      className={classes}
                      type="button"
                    >
                      <ArrowRightIcon className="arrow-icon" />
                      {item.button_text}
                    </button>
                  );
                }

                return (
                  <PrismicNextLink
                    key={index}
                    field={item.button_link}
                    className={classes}
                  >
                    <ArrowRightIcon className="arrow-icon" />
                    {item.button_text}
                  </PrismicNextLink>
                );
              })}
          </div>
        </div>

        {isClient && slice.primary.logos?.length > 0 && (
          <div
            className="relative z-10 max-w-[90rem] mx-auto mt-20 overflow-hidden"
            style={{
              WebkitMaskImage:
                'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
              maskImage:
                'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
              WebkitMaskRepeat: 'no-repeat',
              maskRepeat: 'no-repeat',
            }}
          >
            <Marquee speed={30} gradient={false} play={!isPaused}>
              {doubledLogos.map((item, index) => (
                <div
                  key={index}
                  className="relative flex flex-col items-center w-64 group px-4"
                  onMouseEnter={() => setIsPaused(true)}
                  onMouseLeave={() => setIsPaused(false)}
                >
                  <div className="w-full h-24 relative">
                    {item.logo_tooltip && (
                      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-max max-w-xs p-2 bg-[#ced7d5] backdrop-blur-md text-black text-xs rounded-lg border border-white/20 border-t-2 border-t-[#06f273] shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-20">
                        {item.logo_tooltip}
                        <div className="absolute left-1/2 -translate-x-1/2 top-full w-px h-4 bg-white/50"></div>
                      </div>
                    )}
                    <div className="absolute bottom-[5px] left-0 w-[calc(50%-1rem)] h-px bg-white/20"></div>
                    <div className="absolute bottom-[5px] right-0 w-[calc(50%-1rem)] h-px bg-white/20"></div>
                    <div className="absolute bottom-[-8px] left-1/2 -translate-x-1/2 w-8 h-8 flex items-center justify-center z-10">
                      <div
                        className={`w-3 h-3 bg-background border-2 border-white/20 rounded-full transition-all duration-300 ${isPaused ? 'scale-100' : 'animate-beat'}`}
                      ></div>
                    </div>
                  </div>
                  <div className="mt-4 transition-opacity duration-500 opacity-80 group-hover:opacity-100">
                    {/* Reserve explicit space to avoid CLS */}
                    <div className="relative w-40 h-8">
                      <PrismicNextImage
                        field={item.logo_image}
                        fill
                        className="object-contain"
                        sizes="160px"
                        fallbackAlt=""
                      />
                    </div>
                  </div>
                </div>
              ))}
            </Marquee>
          </div>
        )}
      </section>
    );
  }

  return null;
};

export default HeroCtaWithLogos;
